import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface GalleryImage {
  url: string;
  attribution?: string;
  type?: 'image' | 'video';
  videoId?: string; // YouTube video ID
}

interface ImageGalleryCarouselProps {
  images: GalleryImage[];
  altPrefix: string;
  className?: string;
  autoScrollInterval?: number; // in milliseconds, default 3000ms
  isHero?: boolean; // If true, first image gets fetchpriority="high" for LCP optimization
}

export function ImageGalleryCarousel({ 
  images, 
  altPrefix, 
  className,
  autoScrollInterval = 3000,
  isHero = false
}: ImageGalleryCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setPlayingVideo(false);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setPlayingVideo(false);
  };

  // Auto-scroll effect - pause when video is playing
  useEffect(() => {
    if (images.length <= 1 || isPaused || playingVideo) return;

    const interval = setInterval(goToNext, autoScrollInterval);
    return () => clearInterval(interval);
  }, [images.length, isPaused, playingVideo, goToNext, autoScrollInterval]);

  // Reset playing state when index changes
  useEffect(() => {
    setPlayingVideo(false);
  }, [currentIndex]);

  // Reset currentIndex if it's out of bounds (e.g., images array changed)
  useEffect(() => {
    if (currentIndex >= images.length && images.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, images.length]);

  if (images.length === 0) return null;

  // Safely access current image with bounds check
  const safeIndex = Math.min(currentIndex, images.length - 1);
  const currentImage = images[safeIndex];
  
  if (!currentImage) return null;
  
  const isVideo = currentImage.type === 'video' && currentImage.videoId;

  return (
    <div className={cn("mb-6", className)}>
      {/* Main Image/Video - pause auto-scroll on hover */}
      <div 
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Main Content */}
        <div className="aspect-[16/9] rounded-lg overflow-hidden bg-black">
          {isVideo && playingVideo ? (
            <iframe
              src={`https://www.youtube.com/embed/${currentImage.videoId}?autoplay=1&rel=0`}
              title={`${altPrefix} - Video`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : isVideo ? (
            <>
              <img
                src={currentImage.url}
                alt={`${altPrefix} - Video thumbnail`}
                className="w-full h-full object-cover"
              />
              {/* Play button overlay */}
              <button
                onClick={() => setPlayingVideo(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                aria-label="Play video"
              >
                <div className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 transform group-hover:scale-110 transition-transform shadow-lg">
                  <Play className="h-8 w-8 fill-current" />
                </div>
              </button>
            </>
          ) : (
            <img
              src={currentImage.url}
              alt={`${altPrefix} - Image ${currentIndex + 1}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              // @ts-ignore - fetchpriority is a valid HTML attribute but not in React types yet
              fetchpriority={isHero && currentIndex === 0 ? "high" : undefined}
              loading={isHero && currentIndex === 0 ? "eager" : "lazy"}
            />
          )}
        </div>

        {/* Navigation Arrows - only show if more than 1 item and not playing video */}
        {images.length > 1 && !playingVideo && (
          <>
            <Button
              variant="secondary"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background shadow-lg"
              onClick={goToPrevious}
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background shadow-lg"
              onClick={goToNext}
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Dots indicator - only show if more than 1 item and not playing video */}
        {images.length > 1 && !playingVideo && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((item, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  index === currentIndex
                    ? "bg-white w-4"
                    : "bg-white/50 hover:bg-white/75",
                  item.type === 'video' && "ring-1 ring-red-500"
                )}
                aria-label={`Go to ${item.type === 'video' ? 'video' : 'image'} ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Attribution */}
      {currentImage.attribution && !playingVideo && (
        <p className="text-xs text-muted-foreground mt-1 italic">
          {currentImage.type === 'video' ? 'Video by' : 'Photo provided by'}: {currentImage.attribution}
        </p>
      )}

    </div>
  );
}
